import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { AwbStatus, INVOICE_OPTIONS } from '../types/awbStatus';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const invoiceOptions = INVOICE_OPTIONS.map(opt => ({
  value: opt,
  label: opt
}));

const AwbStatusManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAwbStatus, setEditingAwbStatus] = useState<AwbStatus | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch AWB statuses with server-side filtering and pagination
  const {
    data: awbStatusesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'awbStatuses',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/awbStatus?${params.toString()}`
      );
      const result = response.data.data || {};

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const awbStatuses = awbStatusesResponse?.data || [];
  const pagination = awbStatusesResponse?.pagination;

  // Create mutation
  const createAwbStatusMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/awbStatus', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStatuses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbStatus'],
      });
      toast({
        title: 'Success',
        description: 'AWB Status created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create AWB status';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateAwbStatusMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/awbStatus/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStatuses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbStatus'],
      });
      toast({
        title: 'Success',
        description: 'AWB Status updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingAwbStatus(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update AWB status';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteAwbStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/awbStatus/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['awbStatuses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'awbStatus'],
      });
      toast({
        title: 'Success',
        description: 'AWB Status deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete AWB status',
        variant: 'destructive',
      });
    },
  });

  // Get invoice badge color
  const getInvoiceBadgeClass = (invoice: string) => {
    switch (invoice) {
      case 'ADD IN INVOICE':
        return 'bg-blue-500 hover:bg-blue-600';
      case "DON'T ADD IN INVOICE":
        return 'bg-orange-500 hover:bg-orange-600';
      case "DON'T CALCULATE CHARGE":
        return 'bg-gray-500 hover:bg-gray-600';
      default:
        return '';
    }
  };

  // Table columns configuration
  const columns: TableColumn<AwbStatus>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, awbStatus) => (
        <div className="font-medium">{awbStatus.name}</div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (_, awbStatus) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {awbStatus.code}
        </div>
      ),
    },
    {
      key: 'invoice',
      label: 'Invoice',
      sortable: true,
      render: (_, awbStatus) => (
        <Badge
          variant="default"
          className={getInvoiceBadgeClass(awbStatus.invoice)}
        >
          {awbStatus.invoice}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, awbStatus) => (
        <Badge
          variant={awbStatus.status === 'Active' ? 'default' : 'secondary'}
          className={
            awbStatus.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {awbStatus.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, awbStatus) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(awbStatus.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
    {
      key: 'invoice',
      label: 'Invoice',
      type: 'select',
      options: [
        { value: 'all', label: 'All Invoice Options' },
        ...invoiceOptions,
      ],
      placeholder: 'Filter by invoice',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'AWB Status Information',
      description: 'Configure AWB status details.',
      fields: [
        {
          name: 'name',
          label: 'Status Name',
          type: 'text',
          required: true,
          placeholder: 'Enter AWB status name (e.g., "Delivered", "In Transit")',
          description: 'A unique name for this AWB status',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'code',
          label: 'Status Code',
          type: 'text',
          required: true,
          placeholder: 'Enter AWB status code (e.g., "DEL", "INT")',
          description: 'A unique code for this AWB status',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(50, 'Code cannot exceed 50 characters'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this AWB status',
        },
        {
          name: 'invoice',
          label: 'Invoice Option',
          type: 'radio',
          required: true,
          options: [
            { value: 'ADD IN INVOICE', label: 'Add in Invoice - Include in invoice calculation' },
            { value: "DON'T ADD IN INVOICE", label: "Don't Add in Invoice - Exclude from invoice" },
            { value: "DON'T CALCULATE CHARGE", label: "Don't Calculate Charge - Skip charge calculation" },
          ],
          description: 'How this status affects invoice generation',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingAwbStatus(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (awbStatus: AwbStatus) => {
    setEditingAwbStatus(awbStatus);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (awbStatus: AwbStatus) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${awbStatus.name}"? This action cannot be undone.`
      )
    ) {
      deleteAwbStatusMutation.mutate(awbStatus._id);
    }
  };

  const handleSubmit = async (data: any) => {
    const transformedData = {
      ...data,
      code: data.code.toUpperCase(),
    };

    if (editingAwbStatus) {
      updateAwbStatusMutation.mutate({
        id: editingAwbStatus._id,
        data: transformedData,
      });
    } else {
      createAwbStatusMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAwbStatus(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'AWB Status data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/awbStatus/export?${params.toString()}`
      );

      if (response.data.success) {
        const awbStatuses = response.data.data;

        const exportData = awbStatuses.map((status: AwbStatus) => ({
          Name: status.name,
          Code: status.code,
          Invoice: status.invoice,
          Status: status.status,
          'Created At': new Date(status.createdAt).toLocaleString(),
          'Updated At': new Date(status.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `awb-statuses-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${awbStatuses.length} AWB statuses`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export AWB statuses',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            AWB Status Management
          </h1>
          <p className='text-muted-foreground'>
            Manage AWB statuses and their invoice configurations
          </p>
        </div>
      </div>

      <MasterDataTable<AwbStatus>
        title='AWB Statuses'
        description='Manage and configure AWB status information'
        data={awbStatuses}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search AWB statuses by name or code...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingAwbStatus ? 'Edit AWB Status' : 'Create AWB Status'}
        sections={formSections}
        initialData={editingAwbStatus}
        onSubmit={handleSubmit}
        isLoading={
          createAwbStatusMutation.isPending || updateAwbStatusMutation.isPending
        }
        mode={editingAwbStatus ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default AwbStatusManagement;

